import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { MediaBassUArticle, MediaPrismicArticle } from '@omniafishing/core';
import classNames from 'classnames';
import useEmblaCarousel from 'embla-carousel-react';
import React, { useCallback, useEffect, useState } from 'react';
import iconPlay from '../../assets/icon-play.svg';
import { OmniaButton } from '../../components/omnia_button/omnia_button';
import { DesktopOnly } from '../../components/responsive_elements/responsive_elements';
import { LoadableVideoPlayer } from '../../components/video_player/loadable_video_player';
import VideoPlayer from '../../components/video_player/video_player';
import { useResponsive } from '../../hooks/use_responsive';

interface TechniqueMediaProps {
  youtubeMedia: (MediaPrismicArticle | MediaBassUArticle)[];
}

export const TechniqueMedia = (props: TechniqueMediaProps) => {
  const { youtubeMedia } = props;
  const showThumbs = youtubeMedia.length > 1;

  const { isMobile, isDesktop } = useResponsive();

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    containScroll: false,
  });
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) {
        return;
      }
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) {
      return;
    }
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaMainApi) {
      return;
    }
    emblaMainApi.scrollPrev();
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaMainApi) {
      return;
    }
    emblaMainApi.scrollNext();
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi]);

  useEffect(() => {
    if (!emblaMainApi) {
      return;
    }
    onSelect();

    emblaMainApi.on('select', onSelect).on('reInit', onSelect);
  }, [emblaMainApi, onSelect]);

  return (
    <>
      <div
        className={classNames(
          'overflow-hidden bg-blue mb-3 relative',
          {
            '-mx-3': isMobile,
          },
          isDesktop && 'rounded-lg py-3'
        )}
      >
        {showThumbs && (
          <DesktopOnly>
            <div
              onClick={onPrevButtonClick}
              className="absolute flex items-center z-10 w-[18%] h-full cursor-pointer justify-end top-0"
            >
              <OmniaButton
                shape="circle"
                size="md"
                kind="tertiary"
                type="button"
                onPress={onPrevButtonClick}
              >
                <LeftOutlined />
              </OmniaButton>
            </div>
          </DesktopOnly>
        )}
        <div ref={emblaMainRef}>
          <div className="flex touch-pan-y touch-pinch-zoom">
            {youtubeMedia.map((content) => {
              return (
                <div
                  key={content.uid}
                  className={classNames(
                    'flex-grow-0 flex-shrink-0 flex  text-white gap-2',
                    isMobile && 'px-3 py-3 pb-4 flex-col w-full',
                    isDesktop && 'mx-3 py-9 px-16 flex-row-reverse bg-[#104051] w-[70%]'
                  )}
                >
                  <div className={classNames(isDesktop && 'w-1/2')}>
                    <LoadableVideoPlayer
                      source={{
                        type: 'video',
                        sources: [
                          {
                            src: content.media_item.youtube_url,
                            provider: 'youtube',
                          },
                        ],
                      }}
                      options={{
                        ...VideoPlayer.defaultProps.options,
                        controls: [
                          'play',
                          'play-large',
                          'progress',
                          'current-time',
                          'duration',
                          'mute',
                          'volume',
                          'fullscreen',
                        ],
                      }}
                    />
                  </div>
                  <div className={classNames(isDesktop && 'w-1/2')}>
                    <h3
                      className={classNames(
                        'font-medium ',
                        isMobile && 'text-base',
                        isDesktop && 'text-3xl leading-snug'
                      )}
                    >
                      {content.media_item.title}
                    </h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {showThumbs && (
          <DesktopOnly>
            <div
              onClick={onNextButtonClick}
              className="absolute flex items-center z-10 w-[18%] h-full cursor-pointer right-0 top-0"
            >
              <OmniaButton
                shape="circle"
                size="md"
                kind="tertiary"
                type="button"
                onPress={onNextButtonClick}
              >
                <RightOutlined />
              </OmniaButton>
            </div>
          </DesktopOnly>
        )}
      </div>

      {showThumbs && (
        <div
          ref={emblaThumbsRef}
          className={classNames('overflow-hidden', {
            '-mx-3': isMobile,
          })}
        >
          <div
            className={classNames(
              'flex touch-pan-y touch-pinch-zoom px-2',
              isDesktop && 'justify-center'
            )}
          >
            {youtubeMedia.map((content, index) => {
              return (
                <div
                  key={content.uid}
                  className={classNames(
                    'flex-grow-0 m-1 flex-shrink-0 w-24 cursor-pointer transition-opacity flex flex-col gap-1',
                    {
                      'opacity-50': index !== selectedIndex,
                    },
                    isDesktop && 'w-32 m-2'
                  )}
                  onClick={() => onThumbClick(index)}
                >
                  <div className="relative">
                    <img src={content.media_item.thumbnail} className="rounded-lg" />
                    <img
                      src={iconPlay}
                      className={classNames(
                        'absolute w-1/4 left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2'
                      )}
                    />
                  </div>
                  <DesktopOnly>
                    <p className="text-xs">{content.media_item.title}</p>
                  </DesktopOnly>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};
